import { memo, RefObject, useCallback, useMemo } from "react";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { useShallow } from "zustand/react/shallow";
import { isObject } from "lodash-es";

import { calculateVirtualPadding } from "ds/components/Table/hooks/useStickyRow/helpers";
import useStickyRangeExtractor from "ds/components/Table/hooks/useStickyRow/useStickyRangeExtractor";

import PersonalSettingsSpacesTableRowItem from "../Row/Item";
import PersonalSettingsSpacesTableRowGroup from "../Row/Group";
import usePersonalSettingsSpacesGroupsStore from "../usePersonalSettingsSpacesGroupsStore";
import { selectTableRows } from "../usePersonalSettingsSpacesGroupsStore/tableDataSelectors";
import { selectStickyRanges } from "../usePersonalSettingsSpacesGroupsStore/tableMetaSelectors";

type PersonalSettingsSpacesTableBodyProps = {
  tableWrapperRef: RefObject<HTMLDivElement | null>;
};

const PersonalSettingsSpacesTableBody = ({
  tableWrapperRef,
}: PersonalSettingsSpacesTableBodyProps) => {
  const { tableRows, stickyRanges } = usePersonalSettingsSpacesGroupsStore(
    useShallow((state) => ({
      tableRows: selectTableRows(state),
      stickyRanges: selectStickyRanges(state),
    }))
  );

  const stickyRangesIndexes = useMemo(
    () =>
      stickyRanges.map<[number, number]>(([groupId, lastChildId]) => {
        const groupIndex = tableRows.findIndex((row) => row.id === groupId);
        const lastChildIndex = tableRows.findIndex((row) => row.id === lastChildId);

        return [groupIndex, lastChildIndex];
      }),
    [stickyRanges, tableRows]
  );

  const { rangeExtractor, stickyGroupsVisibleOnTableRef } =
    useStickyRangeExtractor(stickyRangesIndexes);

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => tableWrapperRef.current,
    estimateSize: useCallback(() => 57, []),
    rangeExtractor,
  });

  const virtualizedItems = virtualizer.getVirtualItems();

  // calculate before & after padding to keep the right scroll position
  const [before, after] = useMemo(() => {
    if (!tableRows.length || !virtualizedItems.length) {
      return [0, 0];
    }

    return calculateVirtualPadding(
      virtualizedItems,
      virtualizer.getTotalSize(),
      virtualizer.options.scrollMargin,
      stickyGroupsVisibleOnTableRef.current.size
    );
  }, [virtualizedItems, tableRows, virtualizer, stickyGroupsVisibleOnTableRef]);

  const renderRow = useCallback(
    (virtualRow: VirtualItem | undefined) => {
      if (!virtualRow) return null;

      const row = tableRows[virtualRow.index];

      // Group Row
      if (isObject(row) && "actions" in row) {
        // the key points to the first group index
        // the stickyGroup value points to the last group child index
        const stickyGroup = stickyGroupsVisibleOnTableRef.current.get(virtualRow.index);
        const nextGroupAfterSticky = stickyGroup
          ? virtualizer.measurementsCache[stickyGroup + 1]
          : undefined;
        // the value to detect sticky overlapping starts
        const scrollTopTarget = nextGroupAfterSticky
          ? nextGroupAfterSticky.start - virtualizer.measurementsCache[virtualRow.index]?.size + 1
          : undefined;

        return (
          <PersonalSettingsSpacesTableRowGroup
            key={virtualRow.key}
            rowIndex={virtualRow.index}
            measureRef={virtualizer.measureElement}
            isActiveSticky={!!stickyGroup}
            nextAfterSticky={scrollTopTarget}
            tableRef={tableWrapperRef}
          />
        );
      }

      // Item Row
      return (
        <PersonalSettingsSpacesTableRowItem
          key={virtualRow.key}
          rowIndex={virtualRow.index}
          measureRef={virtualizer.measureElement}
        />
      );
    },
    [
      tableRows,
      virtualizer.measureElement,
      virtualizer.measurementsCache,
      tableWrapperRef,
      stickyGroupsVisibleOnTableRef,
    ]
  );

  return (
    <div style={{ display: "table-row-group" }} role="rowgroup">
      {before > 0 && <div style={{ display: "table-row", height: before }}></div>}
      {virtualizedItems.map(renderRow)}
      {after > 0 && <div style={{ display: "table-row", height: after }}></div>}
    </div>
  );
};

export default memo(PersonalSettingsSpacesTableBody);
